import React, { useEffect, useState, createElement } from 'react';
import { SignaturePad } from './SignaturePad';
import { supabase } from '../lib/supabase';
import { X, Crown, Package, Clock, AlertCircle } from 'lucide-react';
import { SubscriptionTiers } from './SubscriptionTiers';
import { CreditBundles } from './CreditBundles';
import { getSubscriptionStatus, redirectToCustomerPortal, createCheckoutSession } from '../lib/stripe';
import { Modal } from './Modal';
import { SubscriptionStatus } from '../types/subscription';
import { AddressFields } from './AddressFields';
import EmailPreferences from './EmailPreferences';
import { UserProfile } from '../types/user';

interface EditProfileProps {
  user: UserProfile;
  onClose: () => void;
  onUpdate: () => void;
  subscriptionStatus: SubscriptionStatus | null;
  onManageSubscription: () => void;
  showNotification: (message: string, type?: 'success' | 'error', duration?: number) => void;
}

interface CreditPurchase {
  id: string;
  credits_amount: number;
  credits_remaining: number;
  purchase_date: string;
  expiration_date: string;
  days_remaining: number;
}

interface UserCreditsInfo {
  total_credits: number;
  purchases: CreditPurchase[];
}

interface SubscriptionDetails {
  id: string;
  status: string;
  current_period_start: number;
  current_period_end: number;
  cancel_at_period_end: boolean;
  price_id: string;
  product_id: string;
  amount: number;
}

// Move hasActiveSubscription outside the component
function hasActiveSubscription(status?: SubscriptionStatus | null) {
  if (!status?.subscriptionDetails) return false;
  const { status: subStatus } = status.subscriptionDetails;
  return subStatus === 'active' || subStatus === 'trialing';
}

export function EditProfile({ 
  user, 
  onClose, 
  onUpdate, 
  subscriptionStatus, 
  onManageSubscription,
  showNotification 
}: EditProfileProps) {
  const [editedUser, setEditedUser] = useState({
    ...user,
    address_line1: user.address_line1 || '',
    address_city: user.address_city || '',
    address_state: user.address_state || '',
    address_zip: user.address_zip || ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const [showSubscriptionTiers, setShowSubscriptionTiers] = useState(false);
  const [showCreditBundles, setShowCreditBundles] = useState(false);
  const [userCredits, setUserCredits] = useState<number>(0);
  const [creditDetails, setCreditDetails] = useState<CreditPurchase[]>([]);
  const [showCreditDetails, setShowCreditDetails] = useState(false);
  const [subscriptionDetails, setSubscriptionDetails] = useState<SubscriptionDetails | undefined>(undefined);
  const [showEmailChangeModal, setShowEmailChangeModal] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [emailChangeError, setEmailChangeError] = useState('');
  
  // Check if the user has placeholder address values
  const hasPlaceholderAddress = user.address_line1 === 'No address provided' || 
                               user.address_city === 'No city provided' || 
                               user.address_state === 'XX' || 
                               user.address_zip === '00000' ||
                               user.needs_address_update === true;

  // Remove duplicate effect
  useEffect(() => {
    fetchUserCredits();
    
    // Initialize the subscription details from props if available
    if (subscriptionStatus?.subscriptionDetails) {
      setSubscriptionDetails(subscriptionStatus.subscriptionDetails);
      setLoading(false);
    } else {
      setLoading(false);
    }
  }, [subscriptionStatus]);

  // Add real-time subscription for credit updates
  useEffect(() => {
    let subscription: any;

    const setupSubscription = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      subscription = supabase
        .channel('credit_updates')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'credit_updates',
            filter: `user_id=eq.${session.user.id}`
          },
          (payload) => {
            console.log('Credit update received in EditProfile:', payload);
            if (payload.new.credits_data && Array.isArray(payload.new.credits_data)) {
              const creditInfo = payload.new.credits_data[0];
              setUserCredits(creditInfo.total_credits || 0);
              setCreditDetails(creditInfo.purchases || []);
            }
          }
        )
        .subscribe();
    };

    setupSubscription();

    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, []);

  async function fetchUserCredits() {
    try {
      const { data, error } = await supabase.rpc('get_user_credits_with_expiration');
      
      if (error) throw error;
      
      if (data && Array.isArray(data) && data.length > 0) {
        const creditInfo = data[0];  // Get the first object from the array
        setUserCredits(creditInfo.total_credits || 0);
        setCreditDetails(creditInfo.purchases || []);
        console.log('Credits updated in EditProfile:', creditInfo.total_credits, creditInfo.purchases);
      }
    } catch (error) {
      console.error('Error fetching user credits:', error);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    try {
      setSaving(true);
      setError('');

      // Required address fields check
      if (!editedUser.address_line1 || !editedUser.address_city || !editedUser.address_state || !editedUser.address_zip) {
        setError('All address fields are required. Please complete all address fields and try again.');
        setSaving(false);
        return;
      }

      // Validate the state and ZIP code format
      if (editedUser.address_state && editedUser.address_state.length !== 2) {
        setError('State must be a 2-letter code (e.g., CA, NY).');
        setSaving(false);
        return;
      }

      if (editedUser.address_zip && !/^\d{5}$/.test(editedUser.address_zip)) {
        setError('ZIP code must be exactly 5 digits.');
        setSaving(false);
        return;
      }

      // Check if we're updating from placeholder address
      const isAddressUpdated = hasPlaceholderAddress && 
        editedUser.address_line1 !== 'No address provided' &&
        editedUser.address_city !== 'No city provided' &&
        editedUser.address_state !== 'XX' &&
        editedUser.address_zip !== '00000';

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No user session found');

      const { error } = await supabase
        .from('users')
        .upsert({
          user_id: session.user.id,
          ...editedUser,
          // Clear the flag if address was updated from placeholders
          needs_address_update: isAddressUpdated ? false : editedUser.needs_address_update,
          updated_at: new Date().toISOString()
        });

      if (error) {
        console.error('Error updating profile:', error);
        
        // More specific error messages based on the error type
        if (error.message.includes('violates check constraint')) {
          if (error.message.includes('address_state')) {
            setError('State must be a 2-letter code (e.g., CA, NY). Please correct and try again.');
          } else if (error.message.includes('address_zip')) {
            setError('ZIP code must be a 5-digit number. Please correct and try again.');
          } else {
            setError('One or more fields have invalid values. Please check your inputs and try again.');
          }
        } else if (error.message.includes('duplicate key')) {
          setError('An account with this email already exists. Please use a different email address.');
        } else if (error.message.includes('violates not-null constraint')) {
          setError('All address fields are required. Please complete all address fields and try again.');
        } else {
          setError('Failed to save profile changes. Please try again later.');
        }
        
        throw error;
      }
      
      onUpdate();
    } catch (error) {
      console.error('Error updating profile:', error);
      // Error is already set above, no need to set a generic one here
    } finally {
      setSaving(false);
    }
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const { name, value } = e.target;
    setEditedUser((prev: typeof editedUser) => ({
      ...prev,
      [name]: value
    }));
  }

  async function handleSignatureSave(signatureUrl: string) {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No active session');

      const { error: updateError } = await supabase
        .from('users')
        .update({ signature_url: signatureUrl })
        .eq('user_id', session.user.id);

      if (updateError) throw updateError;

      setEditedUser((prev: typeof editedUser) => ({
        ...prev,
        signature_url: signatureUrl
      }));
      setShowSignaturePad(false);
    } catch (error) {
      console.error('Error saving signature:', error);
      setError('Failed to save signature. Please try again.');
    }
  }

  // Add a function to format the date
  function formatDate(dateString: string): string {
    const options: Intl.DateTimeFormatOptions = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  }

  // Add a credit details section to the profile
  const CreditsSection = () => (
    <div className="mb-8 p-4 bg-gray-50 rounded-lg">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-brand-secondary">Your Credits</h3>
        <button
          onClick={() => setShowCreditBundles(true)}
          className="flex items-center gap-2 text-brand-primary hover:text-brand-secondary transition-colors"
        >
          <Package className="w-4 h-4" />
          Buy More Credits
        </button>
      </div>
      
      <div className="mb-4">
        <p className="font-semibold text-xl">
          You have {userCredits} credit{userCredits !== 1 ? 's' : ''} remaining.
        </p>
        <p className="text-sm text-gray-600 mt-1">
          All credits expire 1 year after purchase date. Credits are used in order of expiration (oldest first).
        </p>
      </div>
      
      {creditDetails.length > 0 && (
        <div>
          <h4 className="text-sm font-medium mb-2">Credit Expiration Schedule:</h4>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {creditDetails.map((purchase) => (
              <div key={purchase.id} className="border rounded-lg p-2 text-sm">
                <div className="flex justify-between">
                  <span><strong>{purchase.credits_remaining}</strong> credits remaining</span>
                  <span className={`${purchase.days_remaining < 30 ? 'text-red-500' : 'text-gray-500'}`}>
                    {purchase.days_remaining <= 0 ? (
                      <span className="flex items-center"><AlertCircle className="w-3 h-3 mr-1" /> Expires today</span>
                    ) : (
                      <span className="flex items-center"><Clock className="w-3 h-3 mr-1" /> {purchase.days_remaining} days left</span>
                    )}
                  </span>
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  Expires: {formatDate(purchase.expiration_date)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const handleAddressChange = (field: string, value: string) => {
    setEditedUser((prev: typeof editedUser) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubscriptionTierSelect = async (tier: string) => {
    try {
      const session = await supabase.auth.getSession();
      if (!session.data.session) return;

      await createCheckoutSession('subscription', tier);
    } catch (error) {
      console.error('Error creating checkout session:', error);
      setError('Failed to process subscription. Please try again.');
    }
  };

  const handleCreditBundleSelect = async (bundle: string) => {
    try {
      const session = await supabase.auth.getSession();
      if (!session.data.session) return;

      // Call your credit bundle purchase function here
      console.log('Processing credit bundle purchase:', bundle);
      // Example: await purchaseCreditBundle(bundle, session.data.session.user.id);
    } catch (error) {
      console.error('Error purchasing credit bundle:', error);
      setError('Failed to purchase credits. Please try again.');
    }
  };

  const handleEmailChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailChangeError('');
    
    try {
      const { data, error } = await supabase.auth.updateUser({
        email: newEmail
      });

      if (error) throw error;

      // Show success message
      showNotification('Please check your email to confirm the change.', 'success');
      setShowEmailChangeModal(false);
    } catch (error: any) {
      console.error('Error updating email:', error);
      setEmailChangeError(error.message);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg w-full max-w-lg p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-1/3"></div>
            <div className="h-40 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-semibold text-brand-secondary">Edit Profile</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <X className="w-6 h-6" />
            </button>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Subscription Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-brand-secondary">Subscription</h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Crown className="w-5 h-5 text-brand-primary" />
                    <span className="font-medium">
                      {hasActiveSubscription(subscriptionStatus) ? 'Active Subscription' : 'No Active Subscription'}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={onManageSubscription}
                    className="text-sm px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-secondary transition-colors"
                  >
                    {hasActiveSubscription(subscriptionStatus) ? 'Manage Subscription' : 'Subscribe Now'}
                  </button>
                </div>
              </div>
            </div>

            {/* Credits Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-brand-secondary">Your Credits</h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Package className="w-5 h-5 text-brand-primary" />
                    <span className="font-medium">Available Credits: {userCredits}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setShowCreditBundles(true)}
                      className="text-sm px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-secondary transition-colors"
                    >
                      Buy More Credits
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowCreditDetails(true)}
                      className="text-sm px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-secondary transition-colors"
                    >
                      View Credit History
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Personal Information Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-brand-secondary">Personal Information</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-brand-secondary mb-1">
                    First Name
                  </label>
                  <input
                    type="text"
                    name="first_name"
                    value={editedUser.first_name}
                    onChange={handleChange}
                    className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-brand-secondary mb-1">
                    Last Name
                  </label>
                  <input
                    type="text"
                    name="last_name"
                    value={editedUser.last_name}
                    onChange={handleChange}
                    className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-brand-secondary mb-1">
                    Email
                  </label>
                  <div className="flex gap-2 items-center">
                    <input
                      type="email"
                      name="email"
                      value={editedUser.email}
                      readOnly
                      className="w-full p-2 border rounded-lg bg-gray-50"
                    />
                    <button
                      type="button"
                      onClick={() => setShowEmailChangeModal(true)}
                      className="px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-secondary transition-colors"
                    >
                      Change Email
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Mailing Address Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-brand-secondary">Mailing Address</h3>
              <AddressFields
                address_line1={editedUser.address_line1 || ''}
                address_city={editedUser.address_city || ''}
                address_state={editedUser.address_state || ''}
                address_zip={editedUser.address_zip || ''}
                onChange={handleAddressChange}
                error={error}
              />
            </div>

            {/* Signature Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-brand-secondary">Signature</h3>
              <div>
                {editedUser.signature_url ? (
                  <div className="space-y-2">
                    <img
                      src={editedUser.signature_url}
                      alt="Your signature"
                      className="max-h-24 border rounded p-2"
                    />
                    <button
                      type="button"
                      onClick={() => setShowSignaturePad(true)}
                      className="text-sm text-brand-primary hover:text-brand-secondary"
                    >
                      Update Signature
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setShowSignaturePad(true)}
                    className="inline-flex items-center px-4 py-2 border border-brand-primary text-sm font-medium rounded-md text-brand-primary hover:bg-brand-primary hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary"
                  >
                    Add Signature
                  </button>
                )}
              </div>
            </div>

            {/* Default Message Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-brand-secondary">Default Card Message</h3>
              <div>
                <label htmlFor="default_message" className="block text-sm font-medium text-gray-700">
                  This message will be pre-filled when creating new cards
                </label>
                <textarea
                  id="default_message"
                  name="default_message"
                  value={editedUser.default_message || ''}
                  onChange={handleChange}
                  rows={3}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-primary focus:ring-brand-primary sm:text-sm"
                />
              </div>
            </div>

            {/* Email Preferences Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-brand-secondary">Email Preferences</h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <EmailPreferences />
              </div>
            </div>

            <div className="flex justify-end gap-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-brand-primary text-white rounded-md hover:bg-brand-secondary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>

        {/* Modals */}
        <Modal 
          isOpen={showSignaturePad}
          title="Signature Pad"
          onClose={() => setShowSignaturePad(false)}
        >
          <div className="p-6">
            <SignaturePad onSave={handleSignatureSave} onClose={() => setShowSignaturePad(false)} />
          </div>
        </Modal>

        <Modal 
          isOpen={showSubscriptionTiers}
          title="Subscription Tiers"
          onClose={() => setShowSubscriptionTiers(false)}
        >
          <div className="p-6">
            <SubscriptionTiers 
              onClose={() => setShowSubscriptionTiers(false)} 
              onSelectTier={(tier) => {
                // Handle tier selection
                console.log('Selected tier:', tier);
                setShowSubscriptionTiers(false);
              }}
            />
          </div>
        </Modal>

        <Modal 
          isOpen={showCreditBundles}
          title="Credit Bundles"
          onClose={() => setShowCreditBundles(false)}
        >
          <div className="p-6">
            <CreditBundles 
              onClose={() => setShowCreditBundles(false)}
              onSelectBundle={(bundle) => {
                // Handle bundle selection
                console.log('Selected bundle:', bundle);
                setShowCreditBundles(false);
              }}
            />
          </div>
        </Modal>

        <Modal 
          isOpen={showCreditDetails}
          title="Credit Details"
          onClose={() => setShowCreditDetails(false)}
        >
          <div className="p-6 space-y-4">
            <h3 className="text-lg font-medium text-brand-secondary">Credit Details</h3>
            <div className="space-y-2">
              {creditDetails.map((purchase) => (
                <div key={purchase.id} className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex justify-between">
                    <div>
                      <div className="font-medium">{purchase.credits_amount} Credits</div>
                      <div className="text-sm text-gray-500">
                        {purchase.credits_remaining} remaining
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-500">
                        Expires: {formatDate(purchase.expiration_date)}
                      </div>
                      <div className="text-sm text-gray-500">
                        {purchase.days_remaining} days left
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Modal>

        {/* Add Email Change Modal */}
        {showEmailChangeModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold text-brand-secondary">Change Email Address</h3>
                <button
                  onClick={() => setShowEmailChangeModal(false)}
                  className="text-brand-secondary hover:text-brand-primary"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <form onSubmit={handleEmailChange} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-brand-secondary mb-1">
                    New Email Address
                  </label>
                  <input
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
                    required
                  />
                </div>
                
                <p className="text-sm text-gray-600">
                  A confirmation link will be sent to verify your new email address.
                </p>
                
                {emailChangeError && (
                  <p className="text-red-500 text-sm">{emailChangeError}</p>
                )}
                
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setShowEmailChangeModal(false)}
                    className="px-4 py-2 text-brand-secondary hover:text-brand-primary transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-secondary transition-colors"
                  >
                    Update Email
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}